import { Box, Dialog, DialogContent, DialogTitle } from '@mui/material';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SummaryButton } from '~/components/SummaryButton';
import {
  Indicator,
  IndicatorByUnitResponse,
  IndicatorCategory,
  IndicatorsConfig,
} from '~/services/Http';

import { CategoryIconFormatter } from '../../../../../CategoryIconFormatter';

import { IndicatorEmployeeInfoDialogContent } from './components/IndicatorEmployeeInfoDialogContent';
import { useSxStyles } from './useStyles';

export interface Props {
  indicatorGroup: IndicatorByUnitResponse[];
  category: IndicatorCategory;
  groupButtonWidth: number;
  unitId: number;
}

export const IndicatorGroup: React.FC<Props> = ({
  indicatorGroup,
  category,
  groupButtonWidth,
  unitId,
}) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const [selectedIndicator, setSelectedIndicator] = useState<
    Indicator | undefined
  >();
  const handleIndicatorClick = useCallback(
    (indicator: Indicator) => () => {
      setSelectedIndicator(indicator);
    },
    [setSelectedIndicator],
  );
  const closeIndicatorDetailsDialog = useCallback(() => {
    setSelectedIndicator(undefined);
  }, [setSelectedIndicator]);

  const selectedIndicatorTitle = useMemo(() => {
    if (!selectedIndicator) {
      return undefined;
    }

    return selectedIndicator.category === IndicatorCategory.CUSTOM
      ? selectedIndicator.name
      : `${t(
          `${
            IndicatorsConfig[selectedIndicator.indicatorType]
              .titleTranslationLabel
          }`,
        )} ${Object.values(selectedIndicator.config)[0]} ${
          IndicatorsConfig[selectedIndicator.indicatorType].unitTranslationLabel
        }`;
  }, [selectedIndicator, t]);

  return (
    <Box
      key={category}
      sx={sxStyles.category}
      data-cy={`indicators-group-${category}`}
    >
      <Box sx={sxStyles.categoryTitle}>
        <Box component="span" sx={sxStyles.icon}>
          {<CategoryIconFormatter category={category} />}
        </Box>
        {t(category.toLowerCase())}
      </Box>
      <Box sx={sxStyles.listCategory}>
        {indicatorGroup.map((item) => {
          const label =
            item.indicator.category === IndicatorCategory.CUSTOM
              ? item.indicator.name
              : `${t(
                  `${
                    IndicatorsConfig[item.indicator.indicatorType]
                      .titleTranslationLabel
                  }`,
                )} ${Object.values(item.indicator.config)[0]} ${
                  IndicatorsConfig[item.indicator.indicatorType]
                    .unitTranslationLabel
                }`;

          return (
            <div
              key={item.indicator.id}
              style={{
                width: groupButtonWidth,
                height: groupButtonWidth,
                paddingLeft: 16,
                paddingBottom: 16,
              }}
              onClick={
                !!item.count ? handleIndicatorClick(item.indicator) : undefined
              }
            >
              <SummaryButton
                count={item.count}
                label={label}
                sx={sxStyles.count}
              />
            </div>
          );
        })}
      </Box>

      <Dialog
        open={!!selectedIndicator}
        onClose={closeIndicatorDetailsDialog}
        data-cy="all-list-widget-dialog"
      >
        <DialogTitle>{selectedIndicatorTitle}</DialogTitle>
        <DialogContent sx={sxStyles.dialogContentRoot}>
          {selectedIndicator && (
            <IndicatorEmployeeInfoDialogContent
              indicatorId={selectedIndicator.id}
              unitId={unitId}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
